import {
  IAcademicsCourses,
  IAcademicsClasses,
  EAcademicStream,
  ESubjectType,
} from '../../../../../apis/academics/academicsClassesAndCourses.interfaces';
import { GridColDef } from '@mui/x-data-grid';
import { Box, Chip, Typography, MenuItem } from '@mui/material';
import { SelectOption } from './academicsClassesAndCourses.interfaces';
import { TTranslateTextType } from 'src/hooks/useTranslationHook';

export const getClassesGridColumns = (
  translateText: TTranslateTextType
): GridColDef[] => {
  return [
    {
      field: 'name',
      headerName: translateText(
        'academics.classesAndCourses.columns.className'
      ),
      width: 180,
      renderCell: (params) => (
        <Typography
          variant="body2"
          fontWeight="medium"
          sx={{ display: 'flex', alignItems: 'center', height: '100%' }}
        >
          {(params.row as unknown as IAcademicsClasses).name}
        </Typography>
      ),
    },
    {
      field: 'standardLevel',
      headerName: translateText('academics.classesAndCourses.columns.standard'),
      width: 120,
      renderCell: (params) => (
        <span>
          {(params.row as unknown as IAcademicsClasses).standardLevel}
        </span>
      ),
    },
    {
      field: 'section',
      headerName: translateText('academics.classesAndCourses.columns.section'),
      width: 100,
      renderCell: (params) => {
        const val = (params.row as unknown as IAcademicsClasses).section;
        return val ? (
          <Chip label={val} size="small" color="primary" variant="outlined" />
        ) : (
          '-'
        );
      },
    },
    {
      field: 'stream',
      headerName: translateText('academics.classesAndCourses.columns.stream'),
      width: 150,
      renderCell: (params) => {
        const streamValue = (params.row as unknown as IAcademicsClasses).stream;
        const getChipColor = (stream?: EAcademicStream) => {
          switch (stream) {
            case EAcademicStream.SCIENCE:
              return 'info';
            case EAcademicStream.COMMERCE:
              return 'success';
            case EAcademicStream.ARTS:
              return 'secondary';
            case EAcademicStream.VOCATIONAL:
              return 'warning';
            default:
              return 'default';
          }
        };
        return streamValue ? (
          <Chip
            label={streamValue}
            size="small"
            color={getChipColor(streamValue)}
          />
        ) : (
          '-'
        );
      },
    },
    {
      field: 'board',
      headerName: translateText('academics.classesAndCourses.columns.board'),
      width: 150,
      renderCell: (params) => {
        const boardValue = (params.row as unknown as IAcademicsClasses).board;
        return boardValue ? (
          <Chip label={boardValue} size="small" variant="outlined" />
        ) : (
          '-'
        );
      },
    },
    {
      field: 'academicYear',
      headerName: translateText(
        'academics.classesAndCourses.columns.academicYear'
      ),
      width: 150,
    },
    {
      field: 'classTutor',
      headerName: translateText(
        'academics.classesAndCourses.columns.classTeacher'
      ),
      width: 150,
      renderCell: (params) => {
        const tutorId = (params.row as unknown as IAcademicsClasses).classTutor;
        return tutorId
          ? `${translateText('academics.classesAndCourses.teacherId')}: ${tutorId}`
          : translateText('academics.classesAndCourses.notAssigned');
      },
    },
    {
      field: 'strength',
      headerName: translateText('academics.classesAndCourses.columns.students'),
      width: 120,
      renderCell: (params) => {
        const strength = (params.row as unknown as IAcademicsClasses).strength;
        return (
          <Chip
            label={strength || 0}
            size="small"
            color={strength ? 'success' : 'default'}
          />
        );
      },
    },
    {
      field: 'description',
      headerName: translateText(
        'academics.classesAndCourses.columns.description'
      ),
      width: 200,
      renderCell: (params) => {
        const description = (params.row as unknown as IAcademicsClasses)
          .description;
        return (
          <Typography
            variant="body2"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {description || '-'}
          </Typography>
        );
      },
    },
    {
      field: 'courseIds',
      headerName: translateText('academics.classesAndCourses.columns.courses'),
      width: 130,
      renderCell: (params) => {
        const courseIds =
          (params.row as unknown as IAcademicsClasses).courseIds || [];
        const courseCount = courseIds.length;
        return (
          <Chip
            label={`${courseCount} ${
              courseCount === 1
                ? translateText('academics.classesAndCourses.course')
                : translateText('academics.classesAndCourses.courses')
            }`}
            size="small"
            color={courseCount > 0 ? 'info' : 'default'}
          />
        );
      },
    },
    {
      field: 'updatedAt',
      headerName: translateText(
        'academics.classesAndCourses.columns.lastUpdated'
      ),
      width: 150,
      renderCell: (params) => {
        const updatedAt = (params.row as unknown as IAcademicsClasses)
          .updatedAt;
        if (!updatedAt) return '-';
        const date = new Date(updatedAt);
        return date.toLocaleDateString();
      },
    },
    {
      field: 'actions',
      headerName: translateText('academics.classesAndCourses.columns.actions'),
      width: 180,
      sortable: false,
      filterable: false,
      renderCell: () => (
        <Box
          sx={{ display: 'flex', alignItems: 'center', height: '100%', gap: 1 }}
        >
          <Chip
            label={translateText('common.edit')}
            size="small"
            color="primary"
            clickable
          />
          <Chip
            label={translateText('common.view') || 'View'}
            size="small"
            color="info"
            clickable
          />
        </Box>
      ),
    },
  ];
};

export const getCoursesGridColumns = (
  translateText: TTranslateTextType
): GridColDef[] => {
  return [
    {
      field: 'name',
      headerName: translateText(
        'academics.classesAndCourses.columns.courseName'
      ),
      width: 200,
      renderCell: (params) => (
        <Typography
          variant="body2"
          fontWeight="medium"
          sx={{ display: 'flex', alignItems: 'center', height: '100%' }}
        >
          {(params.row as unknown as IAcademicsCourses).name}
        </Typography>
      ),
    },
    {
      field: 'subjectCode',
      headerName: translateText(
        'academics.classesAndCourses.columns.subjectCode'
      ),
      width: 130,
      renderCell: (params) => (
        <Chip
          label={(params.row as unknown as IAcademicsCourses).subjectCode}
          size="small"
          color="default"
          variant="outlined"
        />
      ),
    },
    {
      field: 'subjectType',
      headerName: translateText(
        'academics.classesAndCourses.columns.subjectType'
      ),
      width: 150,
      renderCell: (params) => {
        const subjectType = (params.row as unknown as IAcademicsCourses)
          .subjectType;
        const getChipColor = (type?: ESubjectType) => {
          switch (type) {
            case ESubjectType.SCIENCE:
              return 'info';
            case ESubjectType.MATHEMATICS:
              return 'secondary';
            case ESubjectType.LANGUAGE:
              return 'success';
            case ESubjectType.COMPUTER:
              return 'warning';
            case ESubjectType.ARTS:
              return 'error';
            default:
              return 'default';
          }
        };
        return (
          <Chip
            label={subjectType}
            size="small"
            color={getChipColor(subjectType)}
          />
        );
      },
    },
    {
      field: 'classIds',
      headerName: translateText(
        'academics.classesAndCourses.columns.assignedClasses'
      ),
      width: 180,
      renderCell: (params) => {
        const classCount = (params.row as unknown as IAcademicsCourses).classIds
          .length;
        return (
          <Chip
            label={`${classCount} ${
              classCount === 1
                ? translateText('academics.classesAndCourses.class')
                : translateText('academics.classesAndCourses.classes')
            }`}
            size="small"
            color={classCount > 0 ? 'success' : 'default'}
          />
        );
      },
    },
    {
      field: 'teacherId',
      headerName: translateText('academics.classesAndCourses.columns.teacher'),
      width: 120,
      renderCell: (params) => {
        const teacherId = (params.row as unknown as IAcademicsCourses)
          .teacherId;
        return teacherId
          ? `${translateText('academics.classesAndCourses.teacherId')}: ${teacherId}`
          : translateText('academics.classesAndCourses.notAssigned');
      },
    },
    {
      field: 'isElective',
      headerName: translateText('academics.classesAndCourses.columns.elective'),
      width: 120,
      renderCell: (params) => (
        <Chip
          label={
            (params.row as unknown as IAcademicsCourses).isElective
              ? translateText('attendance.yes')
              : translateText('attendance.no')
          }
          size="small"
          color={
            (params.row as unknown as IAcademicsCourses).isElective
              ? 'warning'
              : 'default'
          }
          variant="outlined"
        />
      ),
    },
    {
      field: 'weightage',
      headerName: translateText(
        'academics.classesAndCourses.columns.weightage'
      ),
      width: 120,
      renderCell: (params) => {
        const weightage = (params.row as unknown as IAcademicsCourses)
          .weightage;
        return weightage ? `${weightage}%` : '-';
      },
    },
    {
      field: 'practicalsIncluded',
      headerName: translateText(
        'academics.classesAndCourses.columns.practicals'
      ),
      width: 120,
      renderCell: (params) => (
        <Chip
          label={
            (params.row as unknown as IAcademicsCourses).practicalsIncluded
              ? translateText('attendance.yes')
              : translateText('attendance.no')
          }
          size="small"
          color={
            (params.row as unknown as IAcademicsCourses).practicalsIncluded
              ? 'info'
              : 'default'
          }
          variant="outlined"
        />
      ),
    },
    {
      field: 'description',
      headerName: translateText(
        'academics.classesAndCourses.columns.description'
      ),
      width: 200,
      renderCell: (params) => {
        const description = (params.row as unknown as IAcademicsCourses)
          .description;
        return (
          <Typography
            variant="body2"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {description || '-'}
          </Typography>
        );
      },
    },
    {
      field: 'syllabus',
      headerName: translateText('academics.classesAndCourses.columns.syllabus'),
      width: 150,
      renderCell: (params) => {
        const syllabus = (params.row as unknown as IAcademicsCourses).syllabus;
        return syllabus ? (
          <Chip
            label={translateText('attendance.yes')}
            size="small"
            color="success"
            variant="outlined"
          />
        ) : (
          '-'
        );
      },
    },
    {
      field: 'totalLectures',
      headerName: translateText(
        'academics.classesAndCourses.columns.totalLectures'
      ),
      width: 130,
      renderCell: (params) => {
        const totalLectures = (params.row as unknown as IAcademicsCourses)
          .totalLectures;
        return totalLectures ? (
          <Chip
            label={totalLectures}
            size="small"
            color="primary"
            variant="outlined"
          />
        ) : (
          '-'
        );
      },
    },
    {
      field: 'updatedAt',
      headerName: translateText(
        'academics.classesAndCourses.columns.lastUpdated'
      ),
      width: 150,
      renderCell: (params) => {
        const updatedAt = (params.row as unknown as IAcademicsCourses)
          .updatedAt;
        if (!updatedAt) return '-';
        const date = new Date(updatedAt);
        return date.toLocaleDateString();
      },
    },
    {
      field: 'actions',
      headerName: translateText('academics.classesAndCourses.columns.actions'),
      width: 180,
      sortable: false,
      filterable: false,
      renderCell: () => (
        <Box
          sx={{ display: 'flex', alignItems: 'center', height: '100%', gap: 1 }}
        >
          <Chip
            label={translateText('common.edit')}
            size="small"
            color="primary"
            clickable
          />
          <Chip
            label={translateText('common.view') || 'View'}
            size="small"
            color="info"
            clickable
          />
        </Box>
      ),
    },
  ];
};

/**
 * Creates select options array from class list
 * @param classes List of academic classes
 * @param includeAllOption Whether to include an "All Classes" option
 * @returns Array of select options
 */
export const getClassSelectOptions = (
  classes: IAcademicsClasses[],
  includeAllOption: boolean = true,
  translateText?: TTranslateTextType
): SelectOption[] => {
  const options: SelectOption[] = classes.map((cls) => ({
    value: cls.id,
    label: cls.name,
  }));

  // Group and sort by standard level
  options.sort((a, b) => {
    const classA = classes.find((c) => c.id === a.value);
    const classB = classes.find((c) => c.id === b.value);

    if (classA && classB) {
      // First compare by standard level
      if (classA.standardLevel !== classB.standardLevel) {
        return classA.standardLevel.localeCompare(classB.standardLevel);
      }

      // If same standard level, compare by section or name
      return a.label.localeCompare(b.label);
    }

    return 0;
  });

  if (includeAllOption) {
    options.unshift({
      value: 'all',
      label: translateText
        ? translateText('academics.classesAndCourses.allClasses')
        : 'All Classes',
    });
  }

  return options;
};

export const getSectionSelectOptions = (
  classes: IAcademicsClasses[]
): SelectOption[] => {
  const sectionMap = new Map<string, string>();
  classes.forEach((cls) => {
    if (cls.sections?.length) {
      cls.sections.forEach((s) => sectionMap.set(s.id, s.name));
    } else if (cls.section) {
      sectionMap.set(cls.section, cls.section);
    }
  });

  const options: SelectOption[] = Array.from(sectionMap.entries())
    .sort((a, b) => a[1].localeCompare(b[1]))
    .map(([id, name]) => ({
      value: id,
      label: `Section ${name}`,
    }));

  options.unshift({
    value: 'all',
    label: 'All Sections',
  });

  return options;
};

/**
 * Creates select options array from course list
 * @param courses List of academic courses
 * @param includeAllOption Whether to include an "All Courses" option
 * @returns Array of select options
 */
export const getCourseSelectOptions = (
  courses: IAcademicsCourses[],
  includeAllOption: boolean = true,
  translateText?: TTranslateTextType
): SelectOption[] => {
  const options: SelectOption[] = courses.map((course) => ({
    value: course.id,
    label: `${course.name} (${course.subjectCode})`,
  }));

  // Sort alphabetically by name
  options.sort((a, b) => a.label.localeCompare(b.label));

  if (includeAllOption) {
    options.unshift({
      value: 'all',
      label: translateText
        ? translateText('academics.classesAndCourses.allCourses')
        : 'All Courses',
    });
  }

  return options;
};

/**
 * Renders select menu items from select options
 * @param options Array of select options
 * @returns Array of MenuItem components
 */
export const renderSelectMenuItems = (options: SelectOption[]) => {
  return options.map((option) => (
    <MenuItem key={option.value} value={option.value}>
      {option.label}
    </MenuItem>
  ));
};
